import { escapeHtml } from "./escapeHtml";
import { pieceIcon } from "./pieceIcons";
import {
  ASSISTANT_ICON_SVG,
  HOME_ICON_SVG,
  MENU_ICON_SVG,
  OPTIONS_ICON_SVG,
  OTHER_GAMES_ICON_SVG,
  QUIT_ICON_SVG,
  RULES_ICON_SVG,
} from "./cornerIcons";
import { isFullscreen, toggleFullscreen } from "./fullscreen";
import { showConfirmModal } from "./confirmModal";
import { goHome } from "./goHome";
import { showOtherGamesModal } from "./otherGamesModal";
import { formatClockTime } from "../game/clock";
import type { Color, Piece } from "../game/types";

/** En dessous de ce seuil (ms), l'affichage du chrono passe en alerte visuelle. */
const LOW_TIME_THRESHOLD_MS = 20_000;

export interface HudCallbacks {
  onRules: () => void;
  onOptions: () => void;
  onResign: () => void;
  onAssistant: () => void;
}

export class Hud {
  private root: HTMLDivElement;
  private cards: Record<Color, HTMLDivElement>;
  private capturedTrays: Record<Color, HTMLDivElement>;
  private clocks: Record<Color, HTMLDivElement>;
  private turnAnnounce: HTMLDivElement;
  private historyList: HTMLOListElement;

  constructor(names: Record<Color, string>, callbacks: HudCallbacks) {
    this.root = document.createElement("div");
    this.root.id = "hud";
    this.root.innerHTML = `
      <div class="game-header">
        <div class="player-card wood-panel" data-color="w">
          <div class="avatar">${pieceIcon("w", "k")}</div>
          <div class="name">${escapeHtml(names.w)}</div>
          <div class="clock" data-clock="w" hidden></div>
          <div class="captured" data-tray="w"></div>
        </div>
        <div class="player-card wood-panel" data-color="b">
          <div class="avatar">${pieceIcon("b", "k")}</div>
          <div class="name">${escapeHtml(names.b)}</div>
          <div class="clock" data-clock="b" hidden></div>
          <div class="captured" data-tray="b"></div>
        </div>
      </div>
      <div class="turn-announce">
        <div class="name"></div>
        <div class="check-label"></div>
      </div>
      <div class="corner-buttons">
        <div class="corner-buttons-list" id="corner-buttons-list">
          <button class="btn-corner" id="hud-fullscreen-btn" type="button">
            <span class="ico">⛶</span><span class="lbl">Plein ecran</span>
          </button>
          <button class="btn-corner" id="options-btn" type="button">
            <span class="ico">${OPTIONS_ICON_SVG}</span><span class="lbl">Options</span>
          </button>
          <button class="btn-corner" id="assistant-btn" type="button">
            <span class="ico">${ASSISTANT_ICON_SVG}</span><span class="lbl">Assistant</span>
          </button>
          <button class="btn-corner" id="rules-btn" type="button">
            <span class="ico">${RULES_ICON_SVG}</span><span class="lbl">Regles</span>
          </button>
          <button class="btn-corner" id="other-games-btn" type="button">
            <span class="ico">${OTHER_GAMES_ICON_SVG}</span><span class="lbl">Autres jeux</span>
          </button>
          <button class="btn-corner" id="home-btn" type="button">
            <span class="ico">${HOME_ICON_SVG}</span><span class="lbl">Accueil</span>
          </button>
          <button class="btn-corner" id="resign-btn" type="button">
            <span class="ico">${QUIT_ICON_SVG}</span><span class="lbl">Abandonner</span>
          </button>
        </div>
        <button class="btn-corner corner-toggle-btn" id="corner-toggle-btn" type="button" aria-expanded="false" aria-label="Options">
          <span class="ico">${MENU_ICON_SVG}</span>
        </button>
      </div>
      <div class="history-panel wood-panel">
        <h3>Coups joues</h3>
        <ol class="history-list"></ol>
      </div>
    `;
    document.getElementById("ui-root")?.appendChild(this.root);

    this.cards = {
      w: this.root.querySelector('[data-color="w"]')!,
      b: this.root.querySelector('[data-color="b"]')!,
    };
    this.capturedTrays = {
      w: this.root.querySelector('[data-tray="w"]')!,
      b: this.root.querySelector('[data-tray="b"]')!,
    };
    this.clocks = {
      w: this.root.querySelector('[data-clock="w"]')!,
      b: this.root.querySelector('[data-clock="b"]')!,
    };
    this.turnAnnounce = this.root.querySelector(".turn-announce")!;
    this.historyList = this.root.querySelector(".history-list")!;

    this.root.querySelector("#rules-btn")?.addEventListener("click", callbacks.onRules);
    this.root.querySelector("#options-btn")?.addEventListener("click", callbacks.onOptions);
    this.root.querySelector("#resign-btn")?.addEventListener("click", callbacks.onResign);
    this.root.querySelector("#assistant-btn")?.addEventListener("click", callbacks.onAssistant);
    this.root.querySelector("#other-games-btn")?.addEventListener("click", () => showOtherGamesModal());
    // Retour a la racine du site multi-jeux : confirme, comme l'abandon, puisqu'une
    // partie est forcement en cours quand le HUD est affiche.
    this.root.querySelector("#home-btn")?.addEventListener("click", () => {
      void showConfirmModal("Quitter la partie en cours et revenir a l'accueil ?", "Quitter").then((ok) => {
        if (ok) goHome();
      });
    });

    const cornerToggleBtn = this.root.querySelector<HTMLButtonElement>("#corner-toggle-btn")!;
    const cornerButtonsList = this.root.querySelector<HTMLDivElement>("#corner-buttons-list")!;
    cornerToggleBtn.addEventListener("click", () => {
      const open = cornerButtonsList.classList.toggle("open");
      cornerToggleBtn.setAttribute("aria-expanded", String(open));
    });

    const fullscreenBtn = this.root.querySelector<HTMLButtonElement>("#hud-fullscreen-btn")!;
    const syncFullscreenLabel = () => {
      fullscreenBtn.querySelector(".lbl")!.textContent = isFullscreen() ? "Quitter le plein ecran" : "Plein ecran";
    };
    fullscreenBtn.addEventListener("click", () => void toggleFullscreen());
    // Couvre aussi le plein ecran auto (mobile) et la sortie via Echap/geste systeme. Jamais
    // retire : le Hud vit jusqu'au rechargement complet de la page (nouvelle partie = reload).
    document.addEventListener("fullscreenchange", syncFullscreenLabel);
    syncFullscreenLabel();
  }

  setActive(color: Color): void {
    this.cards.w.classList.toggle("active", color === "w");
    this.cards.b.classList.toggle("active", color === "b");
  }

  /** Petite pulsation sur la carte du joueur au trait, pendant que l'IA calcule son coup. */
  setThinking(color: Color, active: boolean): void {
    this.cards[color].classList.toggle("thinking", active);
  }

  /** Revele les deux chronos (caches par defaut) : appele une seule fois si une partie chronometree demarre. */
  showClock(): void {
    this.clocks.w.hidden = false;
    this.clocks.b.hidden = false;
  }

  updateClock(color: Color, remainingMs: number): void {
    const el = this.clocks[color];
    el.textContent = formatClockTime(remainingMs);
    el.classList.toggle("low-time", remainingMs <= LOW_TIME_THRESHOLD_MS);
  }

  setCaptured(color: Color, pieces: readonly Piece[]): void {
    const tray = this.capturedTrays[color];
    tray.innerHTML = pieces.map((p) => `<span class="piece-icon">${pieceIcon(p.color, p.type)}</span>`).join("");
  }

  addHistoryEntry(fullmoveNumber: number, color: Color, san: string): void {
    if (color === "w") {
      const li = document.createElement("li");
      li.innerHTML = `<span class="move-no">${fullmoveNumber}.</span><span class="white-move">${san}</span>`;
      this.historyList.appendChild(li);
    } else {
      const last = this.historyList.lastElementChild;
      const blackSpan = document.createElement("span");
      blackSpan.className = "black-move";
      blackSpan.textContent = san;
      if (last) last.appendChild(document.createTextNode(" ")), last.appendChild(blackSpan);
    }
    this.historyList.scrollTop = this.historyList.scrollHeight;
  }

  showTurnAnnounce(name: string, checkLabel?: string): void {
    this.showAnnounce(`Au tour de ${name}`, checkLabel);
  }

  /**
   * Banniere non bloquante au centre de l'ecran (annonce de tour, mais aussi
   * messages reseau du mode en ligne). `persistent` la laisse affichee jusqu'a
   * `hideAnnounce()` — utile pour "l'adversaire s'est deconnecte...".
   */
  showAnnounce(title: string, subtitle?: string, persistent = false): void {
    const nameEl = this.turnAnnounce.querySelector(".name")!;
    const checkEl = this.turnAnnounce.querySelector(".check-label")!;
    nameEl.textContent = title;
    checkEl.textContent = subtitle ?? "";
    this.turnAnnounce.classList.add("visible");
    window.clearTimeout(this.announceTimer);
    if (persistent) {
      this.announceTimer = undefined;
      return;
    }
    this.announceTimer = window.setTimeout(() => {
      this.turnAnnounce.classList.remove("visible");
    }, 1400);
  }

  hideAnnounce(): void {
    window.clearTimeout(this.announceTimer);
    this.announceTimer = undefined;
    this.turnAnnounce.classList.remove("visible");
  }

  private announceTimer: number | undefined;
}
